<template>
  <section id="service-boxes">
    <h2>{{ t('adServiceBox.title') }}</h2>
    <HgSlider
      :activeSlideIndex="activeSlideIndex"
      :sliderComponents="serviceBoxes"
      type="slide"
      :gap="gap"
      :visibleSlides="visibleSlides"
      :breakpoints="breakpoints"
      @slideChange="activeSlideIndex = $event"
    >
      <template #default="serviceBox">
        <div :class="$style.serviceBoxWrapper">
          <ServiceBox
            :id="serviceBox.id"
            :key="serviceBox.id"
            :icon="serviceBox.icon"
            :title="serviceBox.title"
            :description="serviceBox.description"
            :link="serviceBox.link"
            :maskForBots="serviceBox.maskForBots ?? false"
            @linkClicked="handleServiceBoxLinkClicked(serviceBox)"
          >
          </ServiceBox>
        </div>
      </template>
    </HgSlider>
  </section>
</template>

<script lang="ts" setup>
import { HgSlider } from '@smg-real-estate/domus';
import { ref } from 'vue';

import { useI18n } from 'vue-i18n';
import { ServiceBoxConfig } from './helpers/serviceboxes-helpers';
import ServiceBox from './ServiceBox.vue';

const MOBILE_SCREEN_SIZE_BREAKPOINT = 500;
const TABLET_SCREEN_SIZE_BREAKPOINT = 820;
const DESKTOP_SCREEN_SIZE_BREAKPOINT = 1024;

const VISIBLE_SLIDES_4 = 4;
const VISIBLE_SLIDES_3 = 3;
const VISIBLE_SLIDES_2 = 2;
const VISIBLE_SLIDES_1 = 1;

const { t } = useI18n();

defineOptions({
  name: 'ServiceBoxes',
});

interface Props {
  serviceBoxes: ServiceBoxConfig[];
}
const props = defineProps<Props>();

const emit = defineEmits<{
  serviceBoxClicked: [serviceBox: ServiceBoxConfig];
}>();

const activeSlideIndex = ref(0);
const visibleSlides = VISIBLE_SLIDES_4;
const gap = 8;
const breakpoints = {
  [MOBILE_SCREEN_SIZE_BREAKPOINT]: {
    perView: VISIBLE_SLIDES_1,
    peek: {
      before: 0,
      after: 110,
    },
  },
  [TABLET_SCREEN_SIZE_BREAKPOINT]: {
    perView: VISIBLE_SLIDES_2,
    peek: {
      before: 0,
      after: 70,
    },
  },
  [DESKTOP_SCREEN_SIZE_BREAKPOINT]: {
    perView: VISIBLE_SLIDES_3,
    peek: {
      before: 0,
      after: 100,
    },
  },
};
const handleServiceBoxLinkClicked = (serviceBox: ServiceBoxConfig): void => {
  emit('serviceBoxClicked', serviceBox);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$shadow-spacing: 0.75rem;

.serviceBoxWrapper {
  // adds back negative margin reserved for shadow to avoid overflow
  margin: $shadow-spacing;
}
</style>
